/**
 * VirtualKeyboard.js — TAV POS
 *
 * Widget lateral + panel de teclado que se desliza desde abajo.
 * No modifica ningún componente existente: detecta el input activo
 * globalmente vía eventos de focus y escribe en él usando el setter
 * nativo del prototipo para que React reciba el cambio correctamente.
 *
 * Para excluir un contenedor (ej. componentes con keypad propio):
 *   <div data-no-vkb> ... </div>
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import '../styles/VirtualKeyboard.css';

// ── Layouts ──────────────────────────────────────────────────────────────────

const QWERTY_ROWS = [
  ['q','w','e','r','t','y','u','i','o','p'],
  ['a','s','d','f','g','h','j','k','l'],
  ['SHIFT','z','x','c','v','b','n','m','BACK'],
  ['123','SPACE','ENTER'],
];

const NUM_ROWS = [
  ['1','2','3'],
  ['4','5','6'],
  ['7','8','9'],
  ['.','0','BACK'],
  ['ABC','SPACE','ENTER'],
];

// ── Tipos de input a excluir ─────────────────────────────────────────────────

const EXCLUDED_TYPES = new Set([
  'file','hidden','checkbox','radio','range','color',
  'submit','button','reset','image',
]);

// ── Escribir en un controlled input de React ─────────────────────────────────

function writeToReactInput(el, value) {
  if (!el) return;
  try {
    const proto =
      el.tagName === 'TEXTAREA'
        ? window.HTMLTextAreaElement.prototype
        : window.HTMLInputElement.prototype;
    const setter = Object.getOwnPropertyDescriptor(proto, 'value').set;
    setter.call(el, value);
    el.dispatchEvent(new Event('input', { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
  } catch {
    el.value = value;
    el.dispatchEvent(new Event('input', { bubbles: true }));
  }
}

// Encuentra el ancestro position:fixed más cercano de `el` (normalmente el
// contenedor raíz de un modal abierto). Se usa como destino del portal del
// teclado en vez de document.body siempre: en al menos un dispositivo real,
// el teclado (siempre portado a document.body, con z-index altísimo) se
// seguía pintando DETRÁS de un modal abierto después, a pesar del z-index
// —bug de apilado del navegador/dispositivo, no de este código—. Anidando
// el teclado como hijo del propio contenedor fixed del modal, ambos quedan
// en el MISMO contexto de apilamiento: ya no hace falta ganarle a otro
// contexto por z-index, solo con montarse después (que ya pasa solo) queda
// encima. Si no hay ningún modal abierto (el input vive directo en la
// página), se sigue usando document.body como antes.
function getFixedAncestor(el) {
  let node = el instanceof Element ? el.parentElement : null;
  while (node && node !== document.body) {
    if (window.getComputedStyle(node).position === 'fixed') return node;
    node = node.parentElement;
  }
  return document.body;
}

// ── Componente ────────────────────────────────────────────────────────────────

const VirtualKeyboard = () => {
  const activeEl    = useRef(null);   // input/textarea con foco activo
  const [visible,   setVisible]   = useState(false);
  const [numMode,   setNumMode]   = useState(false);
  const [shift,     setShift]     = useState(false);
  const [hasInput,  setHasInput]  = useState(false);
  const [portalTarget, setPortalTarget] = useState(() =>
    typeof document !== 'undefined' ? document.body : null
  );

  // ── Detectar input activo ───────────────────────────────────────────────

  useEffect(() => {
    // ¿Es un input que este teclado va a gestionar?
    const isManagedInput = (el) => {
      if (!el || !el.tagName) return false;
      const isInput =
        (el.tagName === 'INPUT' && !EXCLUDED_TYPES.has(el.type)) ||
        el.tagName === 'TEXTAREA';
      if (!isInput) return false;
      if (el.closest('[data-no-vkb]')) return false;
      return true;
    };

    // Requerimiento: evitar que Windows muestre su teclado táctil nativo
    // encima del teclado propio (Opción A). inputMode="none" es solo una
    // señal para el teclado táctil del SO/navegador — NO bloquea teclado
    // físico ni la escritura programática que usa este componente
    // (writeToReactInput vía el setter nativo), así que es seguro.
    const suppressNativeKeyboard = (el) => {
      if (el.getAttribute('inputmode') !== 'none') {
        el.setAttribute('inputmode', 'none');
      }
    };

    // Se marca también en pointerdown (antes del focus) para cubrir el caso
    // "el usuario toca el campo": ahí es cuando el SO decide si muestra su
    // teclado, así que la marca debe existir antes de que el focus ocurra.
    const onPointerDown = (e) => {
      if (isManagedInput(e.target)) suppressNativeKeyboard(e.target);
    };

    const onFocusIn = (e) => {
      const el = e.target;
      if (!isManagedInput(el)) return;

      // Cubre el caso "autofocus programático" (sin pointerdown previo).
      suppressNativeKeyboard(el);

      activeEl.current = el;
      setHasInput(true);
      setPortalTarget(getFixedAncestor(el));

      // Cambiar modo según tipo de input
      const isNumeric =
        ['number', 'tel'].includes(el.type) ||
        el.inputMode === 'numeric' ||
        el.dataset.vkbNumeric !== undefined;
      setNumMode(isNumeric);

      // Auto-abrir (Opción A): el teclado aparece al tocar cualquier input
      setVisible(true);
    };

    // Requerimiento: el teclado (pestaña + panel) NO debe verse fuera de
    // cuando hay un campo capturando texto — antes se quedaba visible
    // desde el login y para siempre, porque nada apagaba "hasInput" una
    // vez encendido. Al perder el foco, se espera un instante (setTimeout
    // 0) y se revisa dónde quedó el foco real: si es OTRO input
    // gestionado (el usuario solo cambió de campo dentro del mismo
    // formulario), el teclado sigue abierto sin parpadear; si no, se
    // oculta solo. No se usa relatedTarget de focusout porque en algunos
    // navegadores táctiles no llega de forma confiable — leer
    // document.activeElement un instante después sí es consistente.
    const onFocusOut = () => {
      window.setTimeout(() => {
        const active = document.activeElement;
        if (active && isManagedInput(active)) return;
        activeEl.current = null;
        setHasInput(false);
        setVisible(false);
      }, 0);
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
    };
  }, []);

  // ── Manejar pulsación de tecla ──────────────────────────────────────────

  const handleKey = useCallback((key) => {
    const el = activeEl.current;

    switch (key) {
      case 'BACK': {
        if (!el) return;
        const cur = el.value || '';
        writeToReactInput(el, cur.slice(0, -1));
        return;
      }
      case 'ENTER': {
        setVisible(false);
        if (el) {
          el.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true }));
          el.dispatchEvent(new KeyboardEvent('keyup',  { key: 'Enter', bubbles: true }));
        }
        return;
      }
      case 'SPACE': {
        if (!el) return;
        writeToReactInput(el, (el.value || '') + ' ');
        return;
      }
      case 'SHIFT': {
        setShift(s => !s);
        return;
      }
      case '123': {
        setNumMode(true);
        return;
      }
      case 'ABC': {
        setNumMode(false);
        return;
      }
      default: {
        if (!el) return;
        const char = shift ? key.toUpperCase() : key;
        writeToReactInput(el, (el.value || '') + char);
        if (shift) setShift(false); // shift de un solo uso
      }
    }
  }, [shift]);

  // ── Render de una fila de teclas ────────────────────────────────────────

  const renderKey = (key) => {
    let label;
    let extraClass = '';

    switch (key) {
      case 'BACK':
        label     = <i className="bi bi-backspace" />;
        extraClass = 'vkb-key--special';
        break;
      case 'ENTER':
        label     = <i className="bi bi-arrow-return-left" />;
        extraClass = 'vkb-key--enter';
        break;
      case 'SPACE':
        label     = 'espacio';
        extraClass = 'vkb-key--space';
        break;
      case 'SHIFT':
        label     = <i className={`bi bi-shift${shift ? '-fill' : ''}`} />;
        extraClass = `vkb-key--special${shift ? ' vkb-key--shift-on' : ''}`;
        break;
      case '123':
      case 'ABC':
        label     = key;
        extraClass = 'vkb-key--special';
        break;
      default:
        label = shift ? key.toUpperCase() : key;
    }

    return (
      <button
        key={key}
        className={`vkb-key ${extraClass}`}
        onMouseDown={e => e.preventDefault()} // ← impide perder el foco del input
        onClick={() => handleKey(key)}
        tabIndex={-1}
        aria-label={key}
      >
        {label}
      </button>
    );
  };

  const rows = numMode ? NUM_ROWS : QWERTY_ROWS;

  // ── JSX ─────────────────────────────────────────────────────────────────

  const content = (
    // Requerimiento: al meter el teclado DENTRO del contenedor fixed del
    // modal (para arreglar el apilado), quedó como hermano directo del
    // overlay/fondo del modal — y varios modales de esta app cierran al
    // detectar un click en ese fondo (onClick={onClose} en el overlay).
    // Sin cortar la propagación aquí, cualquier tecla presionada burbujea
    // hasta ese overlay y cierra el modal solo. stopPropagation en este
    // wrapper evita que un click dentro del teclado se confunda con un
    // click "fuera" del modal. Es un <div> sin estilo, no interfiere con
    // el position:fixed de sus hijos (el "containing block" real sigue
    // siendo el contenedor fixed del modal, dos niveles arriba).
    <div onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}>
      {/* Pestaña lateral — solo se muestra mientras hay un campo con foco
          (hasInput). Antes se mostraba SIEMPRE, incluso en la pantalla de
          login y sin ningún campo seleccionado, porque nada apagaba
          "hasInput" una vez que se encendía. También sigue al modal
          abierto (safeTarget), no solo el panel: un usuario reportó que,
          con la pestaña siempre en document.body, tocarla mientras había
          un modal abierto a veces cerraba el modal en vez de abrir el
          teclado — el navegador le "atinaba" al overlay del modal por
          debajo en vez de a la pestaña, el mismo bug de apilado/
          hit-testing que afecta al panel. Metiendo la pestaña en el mismo
          contenedor que el modal se evita también eso. */}
      {hasInput && (
        <button
          className="vkb-tab vkb-tab--has-input"
          onClick={() => setVisible(v => !v)}
          onMouseDown={e => e.preventDefault()}
          aria-label="Teclado virtual"
          tabIndex={-1}
          title="Teclado virtual"
        >
          <i className="bi bi-keyboard" />
        </button>
      )}

      {/* Se quitó el overlay transparente de "cerrar al tocar fuera": cubría
          TODA la pantalla (no solo el área detrás del panel), así que
          tocar OTRO campo del mismo formulario (ej. pasar de Nombre a
          Teléfono) tocaba primero este overlay invisible — cerraba el
          teclado en vez de cambiar de campo, y el usuario necesitaba dos
          toques para escribir en el siguiente campo. El botón "✕" del
          encabezado del panel sigue disponible para cerrar el teclado a
          propósito. */}

      {/* Panel del teclado — antes quedaba SIEMPRE montado (oculto solo
          con transform: translateY(100%)) y solo cambiaba de clase al
          abrir/cerrar. En al menos un dispositivo real esto hacía que el
          panel se pintara "atrás" de un modal recién abierto (el teclado
          ya llevaba rato en el DOM desde antes de que el modal existiera,
          y el navegador no volvía a recalcular su orden de apilado por
          encima del modal nuevo). Ahora se monta desde cero cada vez que
          se abre — eso obliga al navegador a componerlo de nuevo por
          encima de todo lo que ya esté en pantalla. Se pierde la
          animación de deslizamiento hacia arriba (aparece de golpe en
          vez de deslizarse), pero el teclado se vuelve confiable. */}
      {visible && (
        <div className={`vkb-panel vkb-panel--open${numMode ? ' vkb-panel--numeric' : ''}`}>
          {/* Cabecera */}
          <div className="vkb-header">
            <span className="vkb-label">Teclado virtual</span>
            <button
              className="vkb-close"
              onMouseDown={e => e.preventDefault()}
              onClick={() => setVisible(false)}
              tabIndex={-1}
              aria-label="Cerrar teclado"
            >
              <i className="bi bi-x-lg" />
            </button>
          </div>

          {/* Teclas */}
          <div className="vkb-keys">
            {rows.map((row, ri) => (
              <div key={ri} className="vkb-row">
                {row.map(renderKey)}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // Todo (pestaña + overlay + panel) va al contenedor fixed del modal
  // abierto (o a document.body si no hay ninguno) — ver getFixedAncestor
  // arriba. safeTarget cubre el caso borde de que el modal se haya
  // cerrado justo mientras el teclado seguía abierto (su nodo ya no
  // estaría en el DOM).
  const safeTarget =
    portalTarget && document.body.contains(portalTarget) ? portalTarget : document.body;

  return ReactDOM.createPortal(content, safeTarget);
};

export default VirtualKeyboard;
